import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Input,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  List,
  ListItem,
  ListIcon,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { getRoles, registerUser, getUserById, updateUser } from '../api/auth';
import { validatePassword, PASSWORD_RULE_TEXT } from '../utils/passwordPolicy';
import {
  sanitizePhoneInput,
  validatePhone,
  PHONE_PLACEHOLDER,
} from '../utils/phoneValidation';
import { getApiErrorMessage } from '../utils/apiError';

const AddUserModal = ({ isOpen, onClose, refetch, userId }) => {
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('');
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setEnable] = useState(false);
  const [roles, setRoles] = useState([]);
  const toast = useToast();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roleData = await getRoles();
        setRoles(roleData);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (userId) {
        try {
          const userData = await getUserById(userId);
          if (!userData) {
            console.warn('User not found, modal will open empty.');
            return; // Kullanıcı bulunamadıysa setState yapma
          }
          setEmail(userData.email || '');
          setUserName(userData.email || '');
          setRole(userData.role || '');
          setFullName(userData.fullName || '');
          setJobTitle(userData.jobTitle || '');
          setPhoneNumber(sanitizePhoneInput(userData.phoneNumber || ''));
          setDescription(userData.description || '');
          setEnable(userData.enabled || false);
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        // Eğer `userId` yoksa (yeni kullanıcı ekleme durumu), inputları temizle
        setEmail('');
        setUserName('');
        setPassword('');
        setConfirmPassword('');
        setRole('');
        setFullName('');
        setJobTitle('');
        setPhoneNumber('');
        setDescription('');
        setEnable(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const passwordCheck = validatePassword(password);
  const phoneCheck = validatePhone(phoneNumber);

  const handleSave = async () => {
    // Email formatını kontrol eden regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !emailRegex.test(email)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!fullName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a full name.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!role) {
      toast({
        title: 'Error',
        description: 'Please select a user role.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!phoneCheck.isValid) {
      toast({
        title: 'Error',
        description: phoneCheck.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      if (userId) {
        await updateUser({
          userId,
          email,
          userName,
          role,
          fullName,
          jobTitle,
          phoneNumber,
          description,
          isEnabled,
        });
        toast({
          title: 'Success',
          description: 'User updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        if (!passwordCheck.isValid) {
          toast({
            title: 'Error',
            description: PASSWORD_RULE_TEXT,
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          return;
        }

        if (password !== confirmPassword) {
          toast({
            title: 'Error',
            description: 'Passwords do not match.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        await registerUser({
          email,
          password,
          role,
          fullName,
          jobTitle,
          phoneNumber,
          description,
          isEnabled,
        });
        toast({
          title: 'Success',
          description: 'User added successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      refetch();
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error, 'Failed to save user.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{userId ? 'Edit User' : 'Add User'}</ModalHeader>
        <ModalBody>
          <FormControl display="flex" alignItems="center" mb={3}>
            <FormLabel mb="0">Enable:</FormLabel>
            <Checkbox
              isChecked={isEnabled}
              onChange={e => setEnable(e.target.checked)}
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>User Email:</FormLabel>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter email"
            />
          </FormControl>

          <FormControl mb={3} isRequired>
            <FormLabel>Full Name:</FormLabel>
            <Input
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Enter full name"
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Job Title:</FormLabel>
            <Input
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
              placeholder="Enter job title (optional)"
            />
          </FormControl>

          <FormControl mb={3} isInvalid={!phoneCheck.isValid}>
            <FormLabel>Phone Number:</FormLabel>
            <Input
              type="tel"
              inputMode="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(sanitizePhoneInput(e.target.value))}
              placeholder="Enter phone number (optional)"
            />
            <FormErrorMessage>
              {phoneCheck.message || `Example: ${PHONE_PLACEHOLDER}`}
            </FormErrorMessage>
          </FormControl>

          {/* <FormControl mb={3}>
            <FormLabel>User Name:</FormLabel>
            <Input
              value={userName}
              onChange={e => setUserName(e.target.value)}
              placeholder="Enter username"
            />
          </FormControl> */}

          {!userId && (
            <>
              <FormControl mb={3}>
                <FormLabel>Password:</FormLabel>
                <Input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  type="password"
                />

                <List spacing={1} mt={2} fontSize="sm">
                  {passwordCheck.rules.map(rule => (
                    <ListItem
                      key={rule.key}
                      color={rule.passed ? 'green.600' : 'gray.500'}
                    >
                      <ListIcon
                        as={rule.passed ? CheckCircleIcon : WarningIcon}
                        color={rule.passed ? 'green.500' : 'gray.400'}
                      />
                      {rule.label}
                    </ListItem>
                  ))}
                </List>
              </FormControl>

              <FormControl mb={3}>
                <FormLabel>Confirm Password:</FormLabel>
                <Input
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  type="password"
                />
              </FormControl>
            </>
          )}

          <FormControl mb={3}>
            <FormLabel>User Group:</FormLabel>
            <Select
              placeholder="Select role"
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              {roles.map(roleItem => (
                <option key={roleItem.name} value={roleItem.name}>
                  {roleItem.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Description:</FormLabel>
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter description"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave}>
            {userId ? 'Update' : 'Save'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddUserModal;
